import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Package, RefreshCw, Plus, Loader2, CheckCircle, Clock, Search, QrCode, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  listEncomendas,
  updateEncomendaStatus,
  deleteEncomenda,
  Encomenda,
  EncomendaStatus,
  ENCOMENDA_STATUS_LABELS,
  ENCOMENDA_STATUS_COLORS,
  ENCOMENDA_STATUS_COLORS_LIGHT,
} from '../../../services/encomendaApi';
import { makeAbsolutePhotoUrl } from '../../../services/api';
import EncomendaFormModal from './EncomendaFormModal';
import EncomendaCheckoutModal from './EncomendaCheckoutModal';
import { useConfirm } from '../../ui/ConfirmDialog';
import EncomendaDetailModal from './EncomendaDetailModal';
import EncomendaQrModal from './EncomendaQrModal';
import toast from 'react-hot-toast';
import { CondoType, formatUnitLabel } from '../../../utils/terminology';
import SortableColumnHeader from '../../SortableColumnHeader';
import {
  DEFAULT_ENCOMENDA_SORT,
  EncomendaSort,
  EncomendaSortKey,
  nextEncomendaSort,
  pickupTime,
  sortEncomendas,
} from '../../../utils/encomendaSort';

interface EncomendaPanelProps {
  open: boolean;
  customerId?: string;
  customerType?: 'condominio' | 'empresa';
  condoType?: CondoType;
  onClose: () => void;
  onOpenUnit?: (unitId: string, unitName: string, blockId?: string) => void;
  onPersonClick?: (personId: string) => void;
  inline?: boolean;
  lightTheme?: boolean;
  /** Customer's departments (empresa_data.departments), used to resolve department_id → name for encomendas not tied to a unit. */
  departments?: { id: string; name: string }[];
}

// Encomendas are tied to either a unit (condominio residents) or a department
// (empresa employees) — never both. department_id only carries a raw ID (it
// references an entry inside the customer's empresa_data JSONB, not a joinable
// SQL table), so the name has to be resolved client-side from the departments list.
function getUnitOrDeptLabel(enc: Encomenda, departments: { id: string; name: string }[], condoType?: CondoType): string {
  if (enc.unit) {
    const streetLine = enc.unit.street?.name
      ? (enc.unit.street_number ? `${enc.unit.street.name}, ${enc.unit.street_number}` : enc.unit.street.name)
      : null;
    const blockLine = formatUnitLabel({ number: enc.unit.number, blockName: enc.unit.block?.name }, condoType);
    return [streetLine, blockLine].filter(Boolean).join(' – ');
  }
  if (enc.department_id) {
    return departments.find(d => d.id === enc.department_id)?.name ?? '—';
  }
  return '—';
}

function useElapsedTime(dateStr: string) {
  const compute = () => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const totalMin = Math.floor(diff / 60000);
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (hours >= 48) return `${Math.floor(hours / 24)}d`;
    if (hours >= 1) return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    return `${Math.max(1, mins)}min`;
  };
  const [elapsed, setElapsed] = useState(compute);
  useEffect(() => {
    setElapsed(compute());
    const t = setInterval(() => setElapsed(compute()), 60000);
    return () => clearInterval(t);
  }, [dateStr]); // eslint-disable-line
  return elapsed;
}

// ── Light-themed status badge classes ─────────────────────────────────────────
/** Was a private copy here; now shared from encomendaApi so every light surface agrees. */
const LIGHT_STATUS_BADGE = ENCOMENDA_STATUS_COLORS_LIGHT;

function ElapsedBadge({ dateStr }: { dateStr: string }) {
  const elapsed = useElapsedTime(dateStr);
  const hours = (Date.now() - new Date(dateStr).getTime()) / 3600000;
  const color = hours > 24 ? 'text-red-500' : hours > 4 ? 'text-amber-500' : 'text-emerald-600';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums', color)}>
      <Clock className="h-3 w-3" />
      {elapsed}
    </span>
  );
}

// ── Side-panel card (used in non-inline mode) ──────────────────────────────────
function EncomendaCard({
  enc, isProcessing, onCardClick, onCheckout, lightTheme, departments = [], condoType,
}: {
  enc: Encomenda;
  isProcessing: boolean;
  condoType?: CondoType;
  onCardClick: (enc: Encomenda) => void;
  onCheckout: (enc: Encomenda, action: 'picked_up' | 'returned') => void;
  lightTheme?: boolean;
  departments?: { id: string; name: string }[];
}) {
  const statusClass = ENCOMENDA_STATUS_COLORS[enc.status];
  const statusLabel = ENCOMENDA_STATUS_LABELS[enc.status];
  const photoUrl = enc.photo_url ? makeAbsolutePhotoUrl(enc.photo_url) : null;
  const unitLabel = getUnitOrDeptLabel(enc, departments, condoType);
  const personLabel = enc.person ? `${enc.person.first_name} ${enc.person.last_name}` : null;
  const isPending = enc.status === 'received' || enc.status === 'notified';

  return (
    <div className={cn('rounded-lg overflow-hidden border', lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800')}>
      <div className="cursor-pointer hover:opacity-80 transition-opacity" onClick={() => onCardClick(enc)}>
        {photoUrl && <img src={photoUrl} alt="encomenda" className="w-full h-20 object-cover" />}
        <div className="p-3 space-y-1">
          <p className={cn('text-xs font-semibold truncate', lightTheme ? 'text-gray-900' : 'text-white')}>{unitLabel}</p>
          {personLabel && <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')}>{personLabel}</p>}
          {enc.transportadora && <p className={cn('text-xs truncate', lightTheme ? 'text-gray-400' : 'text-gray-500')}>{enc.transportadora}</p>}
          <div className="flex items-center gap-2 pt-1">
            <span className={cn('text-xs px-1.5 py-0.5 rounded border font-medium', statusClass)}>{statusLabel}</span>
            {isPending && <ElapsedBadge dateStr={enc.created_at} />}
          </div>
        </div>
      </div>
      {isPending && (
        <div className="flex gap-1.5 px-3 pb-3">
          <button
            onClick={e => { e.stopPropagation(); onCheckout(enc, 'picked_up'); }}
            disabled={isProcessing}
            className={cn('flex-1 py-1.5 rounded text-xs font-semibold transition-colors border',
              lightTheme ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-400 border-emerald-600/30')}
          >
            {isProcessing ? <Loader2 className="h-3 w-3 animate-spin mx-auto" /> : 'Retirada'}
          </button>
          <button
            onClick={e => { e.stopPropagation(); onCheckout(enc, 'returned'); }}
            disabled={isProcessing}
            className={cn('flex-1 py-1.5 rounded text-xs font-semibold transition-colors border',
              lightTheme ? 'bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-200' : 'bg-gray-700/50 hover:bg-gray-700 text-gray-400 border-gray-700')}
          >
            Devolver
          </button>
        </div>
      )}
    </div>
  );
}

type ViewFilter = 'all' | 'active' | 'retrieved';
const PAGE_SIZE = 30;

export default function EncomendaPanel({ open, customerId, customerType, condoType, onClose, onOpenUnit, onPersonClick, inline = false, lightTheme = false, departments = [] }: EncomendaPanelProps) {
  const confirm = useConfirm();
  const [allItems, setAllItems] = useState<Encomenda[]>([]);
  const [sort, setSort] = useState<EncomendaSort>(DEFAULT_ENCOMENDA_SORT);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ViewFilter>('all');
  const [page, setPage] = useState(0);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [checkoutEnc, setCheckoutEnc] = useState<Encomenda | null>(null);
  const [checkoutAction, setCheckoutAction] = useState<'picked_up' | 'returned'>('picked_up');
  const [qrEnc, setQrEnc] = useState<Encomenda | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const resp = await listEncomendas(customerId);
      const sorted = (resp.data || []).sort(
        (a: Encomenda, b: Encomenda) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setAllItems(sorted);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) load();
  }, [open, customerId, load]);

  const handleCheckout = async (status: EncomendaStatus, opts: { signature_data?: string; notes?: string }) => {
    if (!customerId || !checkoutEnc) return;
    setProcessingId(checkoutEnc.id);
    try {
      await updateEncomendaStatus(customerId, checkoutEnc.id, status, opts);
      toast.success(status === 'picked_up' ? 'Retirada registrada!' : 'Devolução registrada!');
      load();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao atualizar status');
      throw err;
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (enc: Encomenda) => {
    if (!customerId) return;
    if (!await confirm({ message: 'Excluir esta encomenda?', variant: 'danger', confirmLabel: 'Excluir' })) return;
    setProcessingId(enc.id);
    try {
      await deleteEncomenda(customerId, enc.id);
      toast.success('Encomenda excluída');
      load();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao excluir');
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = allItems;
    if (filter === 'active') list = list.filter(e => e.status === 'received' || e.status === 'notified');
    else if (filter === 'retrieved') list = list.filter(e => e.status === 'picked_up' || e.status === 'returned');

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(enc => {
        const unitOrDept = getUnitOrDeptLabel(enc, departments, condoType).toLowerCase();
        const person = enc.person ? `${enc.person.first_name} ${enc.person.last_name}`.toLowerCase() : '';
        return (
          unitOrDept.includes(q) || person.includes(q) ||
          (enc.tracking_code?.toLowerCase() ?? '').includes(q) ||
          (enc.transportadora?.toLowerCase() ?? '').includes(q)
        );
      });
    }
    return list;
  }, [allItems, filter, search, departments, condoType]);

  const activeCount = allItems.filter(e => e.status === 'received' || e.status === 'notified').length;

  // Sorted BEFORE paging, so ordering by Retirada reorders the whole list rather than shuffling
  // whichever 30 rows happen to be on this page.
  const sorted = useMemo(
    () => sortEncomendas(filtered, sort, enc => unitLabel(enc)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, sort, departments, condoType]
  );

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = inline ? sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE) : sorted;

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  const unitLabel = (enc: Encomenda) => getUnitOrDeptLabel(enc, departments, condoType);
  const personLabel = (enc: Encomenda) =>
    enc.person ? `${enc.person.first_name} ${enc.person.last_name}` : '—';

  if (!open) return null;

  const divider = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const textPrimary = lightTheme ? 'text-gray-900' : 'text-white';
  const textMuted = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const bgSurface = lightTheme ? 'bg-white' : 'bg-gray-950';
  const bgHover = lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-800/40';

  return (
    <>
      <EncomendaFormModal open={showForm} customerId={customerId} customerType={customerType} condoType={condoType} onClose={() => setShowForm(false)} onSuccess={load} />
      <EncomendaDetailModal
        open={!!detailId}
        encomendaId={detailId}
        customerId={customerId}
        onClose={() => setDetailId(null)}
        onStatusChange={load}
        onOpenUnit={onOpenUnit ? (unitId, unitName, blockId) => { setDetailId(null); onOpenUnit(unitId, unitName, blockId); } : undefined}
        onPersonClick={onPersonClick ? (personId) => { setDetailId(null); onPersonClick(personId); } : undefined}
        departments={departments}
        condoType={condoType}
      />
      <EncomendaCheckoutModal
        open={!!checkoutEnc}
        encomenda={checkoutEnc}
        action={checkoutAction}
        customerId={customerId}
        condoType={condoType}
        onConfirm={handleCheckout}
        onClose={() => setCheckoutEnc(null)}
      />
      <EncomendaQrModal open={!!qrEnc} encomenda={qrEnc} condoType={condoType} onClose={() => setQrEnc(null)} />

      <div className={inline
        ? cn('h-full flex flex-col', bgSurface)
        : 'fixed inset-0 z-50 flex items-center justify-end pointer-events-none'
      }>
        {!inline && <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={onClose} />}

        <div className={inline
          ? 'flex-1 flex flex-col min-h-0'
          : cn('relative w-full max-w-sm h-full border-l flex flex-col pointer-events-auto shadow-2xl', bgSurface, `border-${divider}`)
        }>

          {/* ── Header ── */}
          <div className={cn('flex items-center gap-2 px-4 py-3 border-b flex-shrink-0', divider)}>
            <Package className={cn('h-4 w-4 text-amber-500 flex-shrink-0')} />
            <span className={cn('font-bold text-xs uppercase tracking-wide flex-shrink-0', textPrimary)}>Encomendas</span>
            {activeCount > 0 && (
              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white flex-shrink-0">
                {activeCount}
              </span>
            )}

            {/* Search */}
            <div className={cn('relative flex-1', inline ? 'max-w-xs' : '')}>
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                placeholder="Buscar unidade, nome, rastreio..."
                className={cn(
                  'w-full border rounded-lg pl-8 pr-3 py-1.5 text-xs focus:outline-none',
                  lightTheme
                    ? 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-gray-400'
                    : 'bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-gray-500'
                )}
              />
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setShowForm(true)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                  lightTheme ? 'bg-gray-900 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-white text-gray-900'
                )}
              >
                <Plus className="h-3.5 w-3.5" />
                Registrar
              </button>
              <button onClick={load} disabled={loading} className={cn('p-1.5 rounded transition-colors', textMuted, lightTheme ? 'hover:text-gray-800' : 'hover:text-white')}>
                <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              </button>
              <button onClick={onClose} className={cn('p-1.5 rounded transition-colors', textMuted, lightTheme ? 'hover:text-gray-800' : 'hover:text-white')}>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ── Filter bar ── */}
          <div className={cn('flex items-center gap-1 px-4 py-2 border-b flex-shrink-0', divider)}>
            {([
              { value: 'all',       label: 'Todos' },
              { value: 'active',    label: 'Ativos' },
              { value: 'retrieved', label: 'Retirados' },
            ] as { value: ViewFilter; label: string }[]).map(opt => (
              <button
                key={opt.value}
                onClick={() => { setFilter(opt.value); setPage(0); }}
                className={cn(
                  'px-3 py-1 rounded-lg text-xs font-semibold border transition-colors',
                  filter === opt.value
                    ? lightTheme ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-900 border-gray-100'
                    : lightTheme ? 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700' : 'bg-transparent text-gray-500 border-gray-700 hover:border-gray-500 hover:text-gray-300'
                )}
              >
                {opt.label}
              </button>
            ))}
            {search && (
              <span className={cn('ml-1 text-xs', textMuted)}>
                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {loading && allItems.length === 0 ? (
              <div className="flex items-center justify-center flex-1">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 gap-2">
                <CheckCircle className={cn('h-8 w-8', lightTheme ? 'text-gray-200' : 'text-gray-700')} />
                <p className={cn('text-sm', textMuted)}>
                  {search ? 'Nenhum resultado encontrado' : 'Nenhuma encomenda'}
                </p>
              </div>
            ) : inline ? (
              /* ── Inline table layout ── */
              <div className="flex-1 overflow-auto flex flex-col min-h-0">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className={cn('border-b', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-700')}>
                      {([
                        ['destino', 'Unidade/Depto / Morador'],
                        ['transportadora', 'Transportadora'],
                        ['status', 'Status'],
                        ['recebida', 'Recebida'],
                        ['retirada', 'Retirada'],
                      ] as [EncomendaSortKey, string][]).map(([key, label]) => (
                        <SortableColumnHeader
                          key={key}
                          label={label}
                          sortKey={key}
                          sort={sort}
                          onSort={k => { setSort(current => nextEncomendaSort(current, k)); setPage(0); }}
                          className={cn('text-[11px]', textMuted)}
                          activeClassName={lightTheme ? 'text-gray-900' : 'text-white'}
                        />
                      ))}
                      <th className="px-3 py-2 w-40" />
                    </tr>
                  </thead>
                  <tbody className={cn('divide-y', lightTheme ? 'divide-gray-100' : 'divide-gray-800')}>
                    {paged.map(enc => {
                      // Controls own their click; the row opens the package. Previously the whole
                      // Unidade/Morador cell and the whole actions cell called stopPropagation,
                      // so clicking the unit text (when it is not a link), the resident's name
                      // (when there is no person to open) or the empty space beside the hover
                      // actions did nothing at all.
                      const own = (handler: () => void) => (e: React.MouseEvent) => {
                        e.stopPropagation();
                        handler();
                      };
                      const isProcessing = processingId === enc.id;
                      const isPending = enc.status === 'received' || enc.status === 'notified';
                      const statusBadge = lightTheme ? LIGHT_STATUS_BADGE[enc.status] : cn('border font-medium text-[11px] px-1.5 py-0.5 rounded', ENCOMENDA_STATUS_COLORS[enc.status]);

                      return (
                        <tr
                          key={enc.id}
                          className={cn('cursor-pointer transition-colors group', bgHover)}
                          onClick={() => setDetailId(enc.id)}
                        >
                          <td className="px-3 py-2.5">
                            <div className="flex flex-col gap-0.5">
                              {onOpenUnit && enc.unit ? (
                                <button
                                  onClick={own(() => onOpenUnit(
                                    enc.unit!.id,
                                    enc.unit!.block?.name ? `${enc.unit!.block.name} / ${enc.unit!.number}` : enc.unit!.number,
                                    enc.unit!.block?.id
                                  ))}
                                  className={cn('font-semibold text-xs hover:text-blue-500 transition-colors text-left whitespace-nowrap', textPrimary)}
                                >
                                  {unitLabel(enc)}
                                </button>
                              ) : (
                                <span className={cn('font-semibold text-xs whitespace-nowrap', textPrimary)}>{unitLabel(enc)}</span>
                              )}
                              {onPersonClick && enc.person ? (
                                <button
                                  onClick={own(() => onPersonClick(enc.person!.id))}
                                  className="text-xs truncate hover:text-blue-500 transition-colors text-left text-gray-500 max-w-[180px]"
                                >
                                  {personLabel(enc)}
                                </button>
                              ) : (
                                <span className={cn('text-xs truncate max-w-[180px]', textMuted)}>{personLabel(enc)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className={cn('text-xs', textMuted)}>{enc.transportadora || <span className={lightTheme ? 'text-gray-300' : 'text-gray-700'}>—</span>}</span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span className={cn('inline-flex text-[11px] px-1.5 py-0.5 rounded border font-semibold w-fit', lightTheme ? LIGHT_STATUS_BADGE[enc.status] : ENCOMENDA_STATUS_COLORS[enc.status])}>
                                {ENCOMENDA_STATUS_LABELS[enc.status]}
                              </span>
                              {isPending && <ElapsedBadge dateStr={enc.created_at} />}
                            </div>
                          </td>
                          <td className={cn('px-3 py-2.5 whitespace-nowrap text-xs tabular-nums', textMuted)}>
                            {formatDate(enc.created_at)}
                          </td>

                          {/* When it was collected — empty for anything still waiting, and for a
                              devolvida package, which was never retirada. */}
                          <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">
                            {pickupTime(enc)
                              ? <span className={lightTheme ? 'text-emerald-700' : 'text-emerald-400'}>{formatDate(pickupTime(enc)!)}</span>
                              : <span className={lightTheme ? 'text-gray-300' : 'text-gray-700'}>—</span>}
                          </td>

                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {isPending && (
                                <>
                                  <button
                                    onClick={own(() => { setCheckoutEnc(enc); setCheckoutAction('picked_up'); })}
                                    disabled={isProcessing}
                                    className={cn('px-2 py-1 rounded text-xs font-semibold border transition-colors', lightTheme ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-400 border-emerald-600/30')}
                                  >
                                    {isProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retirada'}
                                  </button>
                                  <button
                                    onClick={own(() => { setCheckoutEnc(enc); setCheckoutAction('returned'); })}
                                    disabled={isProcessing}
                                    className={cn('px-2 py-1 rounded text-xs font-semibold border transition-colors', lightTheme ? 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200' : 'bg-gray-800 hover:bg-gray-700 text-gray-400 border-gray-700')}
                                  >
                                    Devolver
                                  </button>
                                </>
                              )}
                              <button onClick={own(() => setQrEnc(enc))} className={cn('p-1 rounded transition-colors', lightTheme ? 'text-gray-300 hover:text-amber-500' : 'text-gray-600 hover:text-amber-400')} title="QR Code">
                                <QrCode className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={own(() => handleDelete(enc))} disabled={isProcessing} className={cn('p-1 rounded transition-colors', lightTheme ? 'text-gray-300 hover:text-red-500' : 'text-gray-600 hover:text-red-400')}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className={cn('flex-shrink-0 flex items-center justify-between px-4 py-2 border-t', divider)}>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className={cn('p-1 transition-colors disabled:opacity-30', textMuted, lightTheme ? 'hover:text-gray-800' : 'hover:text-white')}>
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className={cn('text-xs', textMuted)}>{page + 1} / {totalPages} <span className="opacity-50">({filtered.length})</span></span>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className={cn('p-1 transition-colors disabled:opacity-30', textMuted, lightTheme ? 'hover:text-gray-800' : 'hover:text-white')}>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* ── Side panel card list ── */
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {filtered.map(enc => (
                  <EncomendaCard
                    key={enc.id}
                    enc={enc}
                    isProcessing={processingId === enc.id}
                    onCardClick={e => setDetailId(e.id)}
                    onCheckout={(e, action) => { setCheckoutEnc(e); setCheckoutAction(action); }}
                    lightTheme={lightTheme}
                    departments={departments}
                    condoType={condoType}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
